import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Camera, MapPin, AlertCircle, CheckCircle, Loader2, ShieldOff, LogIn, LogOut, KeyRound, HandHelping } from 'lucide-react';
import { cloudinaryAPI, talentAPI } from '../utils/api';
import api from '../utils/api';
import { acquireLocation } from '../utils/geolocation';
import { ensureFreshRuntime, initFreshnessSnapshot } from '../utils/runtimeFreshness';
import { toast } from 'sonner';

/**
 * Talent Clock-In / Clock-Out modal.
 *
 * Flow:
 *   1. Request GPS + camera permissions in parallel.
 *   2. GPS is REQUIRED. If GPS denies/fails, surface a clear error.
 *   3. Selfie is PREFERRED. If the camera errors OR doesn't produce a frame
 *      within 10s, we surface a "Skip Selfie (GPS Only)" button so staff
 *      can still clock in. Cloudinary upload errors do the same.
 *   4. GPS-only submissions send `selfie_failed: true` + `device_info` so
 *      the admin attendance panel flags them for review.
 */
const CAMERA_TIMEOUT_MS = 10000;

const CheckInOutModal = ({ open, onClose, booking, type, onSuccess }) => {
  const [step, setStep] = useState('init'); // init | freshness | capture | uploading | complete
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  // Iter-411n — machine-readable geolocation failure code so the
  // backend + admin queue can distinguish permission-denied vs.
  // timeout vs. position-unavailable rather than a generic "GPS
  // unavailable" string. Values mirror GeolocationPositionError plus
  // 'unsupported' when navigator.geolocation is missing entirely.
  const [locationErrorCode, setLocationErrorCode] = useState(null);
  const [distance, setDistance] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [allowGpsOnly, setAllowGpsOnly] = useState(false);
  // Iter-411n — Event Code panel + local state. Always available as
  // an INDEPENDENT verification path so GPS failure never strands
  // a legitimate onsite worker.
  const [showEventCode, setShowEventCode] = useState(false);
  const [eventCode, setEventCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const cameraTimerRef = useRef(null);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (cameraTimerRef.current) {
      clearTimeout(cameraTimerRef.current);
      cameraTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (open) {
      // Reset on open
      setStep('init');
      setLocation(null);
      setLocationError(null);
      setLocationErrorCode(null);
      setDistance(null);
      setPhoto(null);
      setPhotoPreview(null);
      setUploading(false);
      setError(null);
      setCameraReady(false);
      setCameraError(null);
      setAllowGpsOnly(false);
      setShowEventCode(false);
      setEventCode('');
      setVerifyingCode(false);

      // Iter-411o — Runtime freshness guard. If the current runtime is
      // stale relative to the deployed backend build, reload BEFORE any
      // attendance UI renders so we never repeat Samantha's dead-end.
      (async () => {
        setStep('freshness');
        const verdict = await ensureFreshRuntime({
          type: type,
          bookingId: booking && booking.id ? String(booking.id) : null,
        });
        if (verdict.stale) {
          // A reload is in-flight (or was skipped by the loop guard).
          // Do nothing further — the reload will re-open this modal via
          // the pending-attendance restorer.
          return;
        }
        requestPermissions();
      })();
    }
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Iter-411o — Also revalidate freshness when the PWA resumes from
  // background WHILE the attendance modal is open. Talent leaves their
  // phone screen off in a parking lot, we deploy a fix, they wake the
  // phone — without this we'd keep serving the pre-deploy code.
  useEffect(() => {
    if (!open) return undefined;
    const handler = async () => {
      if (document.visibilityState !== 'visible') return;
      const verdict = await ensureFreshRuntime({
        type,
        bookingId: booking && booking.id ? String(booking.id) : null,
      });
      // If stale, ensureFreshRuntime kicks off the reload; the modal
      // will reopen after page reload via the pending-action restorer.
      // No action needed here.
      void verdict;
    };
    document.addEventListener('visibilitychange', handler);
    window.addEventListener('focus', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
      window.removeEventListener('focus', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, booking]);

  // Iter-411o — Take the initial freshness snapshot on first mount so
  // we have a reference build id to compare against later.
  useEffect(() => {
    initFreshnessSnapshot();
  }, []);

  const startCameraTimeout = () => {
    cameraTimerRef.current = setTimeout(() => {
      if (!cameraReady) {
        setCameraError('Camera is taking too long to start. You can still clock in with GPS only.');
        setAllowGpsOnly(true);
      }
    }, CAMERA_TIMEOUT_MS);
  };

  const requestPermissions = async () => {
    // Iter-411n — GPS is NO LONGER a hard-block. We attempt it first,
    // and if it fails we still let the talent proceed via one of the
    // independent recovery paths (event code, "I'm onsite" fallback,
    // or GPS-only submit if only the camera failed). GPS never gates
    // access to its own fallback — that was Samantha's dead-end.
    //
    // P0 (2026-08-18, Anita Currie / Dallas multi-talent report) —
    // Two-stage acquisition (25s high-accuracy → 8s low-accuracy
    // network fallback) via utils/geolocation.acquireLocation. The
    // prior single-stage 10s high-accuracy call reliably timed out
    // at indoor venues on cellular data.
    try {
      const loc = await acquireLocation();
      setLocation({ lat: loc.latitude, lng: loc.longitude });
      if (booking?.venue_lat && booking?.venue_lng) {
        setDistance(calculateDistance(loc.latitude, loc.longitude, booking.venue_lat, booking.venue_lng));
      }
    } catch (err) {
      console.error('GPS error:', err);
      const codeStr = err?.code || 'unknown';
      setLocationError(err.message || 'GPS location unavailable.');
      setLocationErrorCode(codeStr);
      // Do NOT hard-block — continue to capture step so the recovery
      // paths render (event code, onsite fallback).
    }

    setStep('capture');

    // Iter-213 P0 — iOS Safari 18 hardening:
    //   • Don't auto-request camera on Safari/iOS — wait for a user
    //     gesture ("Start Camera" button). Otherwise getUserMedia can
    //     resolve before the user has interacted, causing autoplay
    //     blocks and the dreaded silent-fail.
    const ua = (navigator.userAgent || '').toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isSafari = /safari/.test(ua) && !/crios|fxios|edgios/.test(ua);
    if (isIOS && isSafari) {
      setCameraReady(false);
      return;
    }
    await startStream();
  };

  const startStream = async () => {
    setCameraError(null);
    try {
      startCameraTimeout();
      const stream = await navigator.mediaDevices.getUserMedia({
        // Iter-213 P0 — 720x720 (square) keeps the captured JPEG small,
        // and matches what we crop server-side. Adding aspectRatio
        // keeps iOS Safari from forcing landscape on the rear cam.
        video: {
          facingMode: 'user',
          width:  { ideal: 720 },
          height: { ideal: 720 },
          aspectRatio: { ideal: 1 },
        },
        audio: false,
      });
      // Iter-411q — Guard against the classic "black screen" scenario:
      // getUserMedia resolves to a MediaStream but the stream contains
      // NO active video tracks (some Android/Chromium builds do this on
      // Meta browser, in-app WebViews, or when a privacy shade is on).
      const tracks = stream.getVideoTracks ? stream.getVideoTracks() : [];
      if (!tracks || tracks.length === 0 || tracks.every((t) => t.readyState === 'ended' || t.muted)) {
        try { stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
        throw Object.assign(new Error('no_video_tracks'), { name: 'NotFoundError' });
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // iOS Safari 18: explicit play() so we surface autoplay errors
        // instead of silently failing.
        try {
          await videoRef.current.play();
        } catch (playErr) {
          console.warn('video.play() rejected (likely autoplay policy):', playErr);
        }
        // Iter-411q — Ready-signal watchdog. `loadedmetadata` alone is
        // not sufficient on iOS 17 / Meta browser — video element can
        // fire the event before any frame renders, leaving a black
        // preview. We accept ANY of these signals as "ready":
        //   * loadedmetadata (metadata parsed)
        //   * playing (first frame committed)
        //   * canplay (media session says frames incoming)
        //   * a manual videoWidth > 0 poll (fires when tracks arrive)
        // If NONE of them fire within 6 seconds we treat it as a
        // black-screen and flip into the camera-error state so the
        // native fallback becomes prominent.
        const markReady = () => {
          if (cameraTimerRef.current) {
            clearTimeout(cameraTimerRef.current);
            cameraTimerRef.current = null;
          }
          setCameraReady(true);
        };
        const check = () => {
          if (videoRef.current && videoRef.current.videoWidth > 0) markReady();
        };
        videoRef.current.onloadedmetadata = check;
        videoRef.current.onplaying       = markReady;
        videoRef.current.oncanplay       = check;
        // Poll videoWidth for browsers that don't fire the events promptly.
        let polls = 0;
        const poll = setInterval(() => {
          polls += 1;
          if (!streamRef.current || cameraReady) { clearInterval(poll); return; }
          if (videoRef.current && videoRef.current.videoWidth > 0) {
            clearInterval(poll);
            markReady();
          } else if (polls > 30) {  // 30 * 200ms = 6s
            clearInterval(poll);
            if (!cameraReady) {
              console.warn('[Camera] black-screen watchdog — no video frames after 6s');
              setCameraError(
                "Your camera opened but didn't show a live preview. Use the "
                + '"Upload / take photo with your phone camera" button below, '
                + "or check in with GPS only. Your attendance is protected."
              );
              setAllowGpsOnly(true);
              try { stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
              streamRef.current = null;
            }
          }
        }, 200);
      }
    } catch (err) {
      console.error('Camera error:', err);
      const name = err?.name || '';
      let msg = 'Camera unavailable. You can still upload a photo from your phone or check in without it.';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        msg = 'Camera access denied. Open Settings → Safari → Camera → Allow, then retry. You can also upload a photo or clock in with GPS only.';
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        msg = 'No usable front camera found. Use the "Upload / take photo" button below, or clock in with GPS only.';
      } else if (name === 'NotReadableError') {
        msg = 'Camera busy (another app is using it). Close other camera apps, or use the "Upload / take photo" button below.';
      }
      setCameraError(msg);
      setAllowGpsOnly(true);
      if (cameraTimerRef.current) {
        clearTimeout(cameraTimerRef.current);
        cameraTimerRef.current = null;
      }
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const srcW = video.videoWidth  || 720;
    const srcH = video.videoHeight || 720;
    // Iter-213 P0 — downscale to max 1024px on the long edge before
    // toBlob. iOS Safari 18 routinely fails toBlob on raw 1920x1080
    // frames (OOM in the JS bridge). 1024px is plenty for face match
    // and keeps the JPEG well under our 8MB backend cap.
    const MAX = 1024;
    const scale = Math.min(1, MAX / Math.max(srcW, srcH));
    const w = Math.round(srcW * scale);
    const h = Math.round(srcH * scale);
    canvas.width  = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        // Iter-213 P0 — toBlob can resolve with null on iOS Safari
        // under memory pressure. Treat that as a capture failure and
        // surface the file-upload fallback CTA.
        if (!blob) {
          setCameraError('Could not capture the photo on this device. Upload a photo instead, or clock in with GPS only.');
          setAllowGpsOnly(true);
          return;
        }
        setPhoto(blob);
        setPhotoPreview(canvas.toDataURL('image/jpeg', 0.7));
        stopCamera();
      },
      'image/jpeg',
      0.7,        // ~150-300KB per frame at 1024px square. Plenty for face match.
    );
  };

  /** Iter-213 P0 — Native file input fallback.
   *  When getUserMedia fails on iOS Safari we surface a hidden
   *  <input type="file" capture="user"> button so the talent can
   *  still take a photo via the OS-level camera (which never fails
   *  the way the WebRTC pipeline does).
   */
  const handleFileFallback = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Photo too large (max 8MB). Try again with a smaller photo.');
      return;
    }
    try {
      // Downscale large user-uploaded photos through canvas before
      // submit so iOS HEIC/4032×3024 jpegs don't crash the upload.
      const img = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => {
          const i = new Image();
          i.onload  = () => resolve(i);
          i.onerror = reject;
          i.src = r.result;
        };
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const MAX = 1024;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const canvas = canvasRef.current || document.createElement('canvas');
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.7));
      if (!blob) {
        toast.error('Could not process the photo on this device. Please try a smaller image.');
        return;
      }
      setPhoto(blob);
      setPhotoPreview(canvas.toDataURL('image/jpeg', 0.7));
      setCameraError(null);
      stopCamera();
    } catch (err) {
      console.error('File fallback failed:', err);
      toast.error('Could not load that photo. Try a different one.');
    }
  };

  const retakePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    setCameraReady(false);
    setCameraError(null);
    requestPermissions();
  };

  const uploadToCloudinary = async () => {
    if (!photo) return null;
    const folder = `checkins/${booking.id}/${type === 'checkin' ? 'checkin' : 'checkout'}`;
    const sigResponse = await cloudinaryAPI.getSignature({ folder, resource_type: 'image' });
    const { signature, timestamp, cloud_name, api_key } = sigResponse.data;
    const formData = new FormData();
    formData.append('file', photo);
    formData.append('signature', signature);
    formData.append('timestamp', timestamp);
    formData.append('api_key', api_key);
    formData.append('folder', folder);
    const uploadResponse = await fetch(
      `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
      { method: 'POST', body: formData }
    );
    if (!uploadResponse.ok) {
      throw new Error(`Cloudinary upload failed (HTTP ${uploadResponse.status})`);
    }
    const result = await uploadResponse.json();
    if (!result.secure_url) throw new Error('Cloudinary returned no URL');
    return result.secure_url;
  };

  const submitClockEvent = async ({ selfie_url, selfie_failed, gps_fallback, gps_fallback_reason }) => {
    const payload = {
      booking_id: booking.id,
      // Iter-411n — lat/lng are now truly optional. When GPS failed we
      // post them as null and rely on `gps_fallback=true` to route the
      // record to admin review without hard-blocking the talent.
      latitude: location?.lat ?? null,
      longitude: location?.lng ?? null,
      selfie_url: selfie_url || null,
      selfie_failed: !!selfie_failed,
      gps_fallback: !!gps_fallback,
      gps_fallback_reason: gps_fallback_reason || null,
      device_info: typeof navigator !== 'undefined' ? (navigator.userAgent || '').slice(0, 500) : null,
    };
    if (type === 'checkin') {
      return talentAPI.checkin(payload);
    }
    return talentAPI.checkout(payload);
  };

  // Iter-411n — Independent "I'm Onsite" recovery path.
  // Works when GPS is denied/failed/unsupported. We post `gps_fallback=true`
  // with NO lat/lng and a machine-readable reason. Never blocks. Backend
  // routes to `gps_fallback_review`, reliability protection skips no-show
  // penalty, and the admin review queue picks it up with device diagnostics.
  const handleOnsiteFallbackSubmit = async () => {
    stopCamera();
    setUploading(true);
    setError(null);
    try {
      await submitClockEvent({
        selfie_url: null,
        selfie_failed: true,
        gps_fallback: true,
        gps_fallback_reason: locationErrorCode || 'gps_unavailable',
      });
      setStep('complete');
      toast.success(
        `${type === 'checkin' ? 'Check-in' : 'Check-out'} request submitted — pending admin verification.`
      );
      setTimeout(() => { onSuccess && onSuccess(); onClose(); }, 1500);
    } catch (err) {
      console.error('Onsite fallback submit error:', err);
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Iter-411n — Event Code verification path.
  // Independent of GPS AND camera — the talent types a 6-char code
  // read out by their onsite lead. Backend verifies against
  // `shifts.onsite_event_code` and check-in is counted as verified.
  const verifyEventCode = async () => {
    if (!eventCode || eventCode.trim().length < 4) {
      toast.error('Enter the event code from your onsite lead.');
      return;
    }
    setVerifyingCode(true);
    setError(null);
    try {
      await api.post('/attendance/verify-event-code', {
        booking_id: booking.id,
        event_code: eventCode.trim().toUpperCase(),
        // Send device info so admins can audit code verifications.
        device_info: typeof navigator !== 'undefined' ? (navigator.userAgent || '').slice(0, 500) : null,
      });
      setStep('complete');
      toast.success(`${type === 'checkin' ? 'Check-in' : 'Check-out'} verified via event code.`);
      setTimeout(() => { onSuccess && onSuccess(); onClose(); }, 1500);
    } catch (err) {
      console.error('Event code error:', err);
      setError(err.response?.data?.detail || 'Event code did not match. Ask your onsite lead to re-read it.');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleSubmitWithSelfie = async () => {
    setUploading(true);
    setError(null);
    let photoUrl = null;
    let fellBack = false;
    try {
      photoUrl = await uploadToCloudinary();
    } catch (uploadErr) {
      console.error('Selfie upload failed:', uploadErr);
      // Don't block the talent — flip into GPS-only fallback automatically.
      toast.error('Selfie upload failed — clocking you in with GPS only. Admin will review.');
      fellBack = true;
    }
    // Iter-411n — if GPS also failed, this becomes an onsite fallback.
    const gpsAvailable = !!location;
    try {
      await submitClockEvent({
        selfie_url: photoUrl,
        selfie_failed: fellBack,
        gps_fallback: !gpsAvailable,
        gps_fallback_reason: !gpsAvailable ? (locationErrorCode || 'gps_unavailable') : null,
      });
      setStep('complete');
      let msg;
      if (!gpsAvailable) {
        msg = `${type === 'checkin' ? 'Check-in' : 'Check-out'} submitted — pending Admin verification (GPS unavailable).`;
      } else if (fellBack) {
        msg = `${type === 'checkin' ? 'Checked in' : 'Checked out'} (GPS only — flagged for review).`;
      } else {
        msg = `${type === 'checkin' ? 'Check-in' : 'Check-out'} successful!`;
      }
      toast.success(msg);
      setTimeout(() => { onSuccess && onSuccess(); onClose(); }, 1500);
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleGpsOnlySubmit = async () => {
    if (!location) {
      // Should not be reachable — the fallback UI gates this button
      // on `{location && ...}` — but keep the guard defensively.
      return handleOnsiteFallbackSubmit();
    }
    stopCamera();
    setUploading(true);
    setError(null);
    try {
      await submitClockEvent({ selfie_url: null, selfie_failed: true });
      setStep('complete');
      toast.success(
        `${type === 'checkin' ? 'Checked in' : 'Checked out'} (GPS only — flagged for admin review).`
      );
      setTimeout(() => { onSuccess && onSuccess(); onClose(); }, 1500);
    } catch (err) {
      console.error('GPS-only submit error:', err);
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const isOutOfRadius = distance && booking.gps_radius_meters && distance > booking.gps_radius_meters;
  const ActionIcon = type === 'checkin' ? LogIn : LogOut;
  const actionLabel = type === 'checkin' ? 'Clock In' : 'Clock Out';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="checkin-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ActionIcon className="w-5 h-5 text-cyan-600" />
            {actionLabel} — {booking.role_name}
          </DialogTitle>
          <DialogDescription>
            {booking.venue_name} • {booking.city}, {booking.state}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <Alert variant="destructive" data-testid="checkin-error-alert">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {locationError && (
            <Alert className="border-amber-400 bg-amber-50" data-testid="gps-error-alert">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-900">
                <strong>GPS unavailable:</strong> {locationError}
                {' '}You can still {type === 'checkin' ? 'check in' : 'check out'} using an event code
                from your onsite lead, or request Admin verification below.
              </AlertDescription>
            </Alert>
          )}

          {step === 'freshness' && (
            <div className="text-center py-10" data-testid="freshness-check">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-cyan-600" />
              <p className="text-base font-medium">Updating attendance tools…</p>
              <p className="text-xs text-slate-500 mt-2">
                Making sure you have the latest onsite check-in features.
              </p>
            </div>
          )}

          {step === 'init' && (
            <div className="text-center py-10">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-cyan-600" />
              <p className="text-base">Requesting location and camera…</p>
            </div>
          )}

          {step === 'capture' && (
            <div className="space-y-4">
              {/* Location info */}
              {distance !== null && (
                <Alert className={isOutOfRadius ? 'border-amber-500 bg-amber-50' : 'border-green-500 bg-green-50'}>
                  <MapPin className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-medium mb-1">
                      Distance from venue: {distance.toFixed(0)} meters
                    </div>
                    <div className="text-sm">
                      {isOutOfRadius ? (
                        <span className="text-amber-700">
                          ⚠️ Outside allowed radius ({booking.gps_radius_meters}m). Will require admin approval.
                        </span>
                      ) : (
                        <span className="text-green-700">
                          ✓ Within allowed radius ({booking.gps_radius_meters}m)
                        </span>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {cameraError && (
                <Alert className="border-amber-400 bg-amber-50" data-testid="camera-error-alert">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-900">
                    <strong>Camera issue:</strong> {cameraError}
                  </AlertDescription>
                </Alert>
              )}

              {/* Camera preview */}
              {!photo && !cameraError && (
                <div className="relative bg-gray-50 rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    data-testid="camera-preview"
                  />
                  {!cameraReady && !streamRef.current && (
                    /* Iter-213 P0 — iOS Safari needs an explicit user
                       gesture to start the camera. requestPermissions
                       skips startStream() on iOS Safari and we surface
                       this button instead. */
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/60 text-white text-sm gap-3">
                      <Camera className="w-10 h-10" />
                      <Button
                        onClick={startStream}
                        size="lg"
                        className="bg-cyan-500 hover:bg-cyan-600 text-black"
                        data-testid="start-camera-button"
                      >
                        Tap to start camera
                      </Button>
                      <span className="text-xs text-white/70">If the prompt asks, allow camera access</span>
                    </div>
                  )}
                  {!cameraReady && streamRef.current && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/40 text-white text-sm">
                      <Loader2 className="w-8 h-8 animate-spin mb-2" />
                      Starting camera…
                    </div>
                  )}
                  {cameraReady && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                      <Button
                        onClick={capturePhoto}
                        size="lg"
                        className="rounded-full bg-white hover:bg-gray-100 text-black h-14 w-14 p-0"
                        data-testid="capture-button"
                      >
                        <Camera className="w-6 h-6" />
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Iter-411q — Native camera/photo picker ALWAYS available.
                  Independent of the embedded WebRTC camera so a black
                  screen, denied permission, or in-app browser cannot
                  strand the talent. Uses `capture="user"` for the OS
                  front-camera flow while allowing gallery selection. */}
              {!photo && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <label
                    className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-md border border-cyan-300 bg-cyan-50 text-sm font-medium text-cyan-900 hover:bg-cyan-100 cursor-pointer"
                    data-testid="file-upload-fallback-label"
                  >
                    <Camera className="w-4 h-4" />
                    Upload / take photo with your phone camera
                    <input
                      type="file"
                      accept="image/*"
                      capture="user"
                      className="hidden"
                      onChange={handleFileFallback}
                      data-testid="file-upload-fallback-input"
                    />
                  </label>
                  {cameraError && (
                    <Button
                      onClick={() => { setCameraError(null); setAllowGpsOnly(false); startStream(); }}
                      variant="outline"
                      className="text-sm"
                      data-testid="retry-camera-button"
                    >
                      <Camera className="w-4 h-4 mr-1" /> Retry camera
                    </Button>
                  )}
                </div>
              )}

              {/* Photo preview + submit */}
              {photo && (
                <div className="space-y-3">
                  <div className="relative bg-gray-50 rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    <img src={photoPreview} alt="Captured" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={retakePhoto} className="flex-1" disabled={uploading}>
                      Retake Photo
                    </Button>
                    <Button
                      onClick={handleSubmitWithSelfie}
                      disabled={uploading}
                      className="flex-1 h-12 text-base bg-cyan-600 hover:bg-cyan-700"
                      data-testid="submit-checkin-button"
                    >
                      {uploading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                      ) : (
                        <>{actionLabel} Now</>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Iter-411n — Recovery paths (GPS-only / Onsite fallback / Event code).
                  Design rule: GPS fallback cannot depend on GPS. Camera
                  fallback cannot depend on the camera. Event code is
                  always available as an independent final recovery path
                  so no talent gets stranded. */}
              {!photo && !showEventCode && (allowGpsOnly || cameraError || locationError) && (
                <div className="border-t pt-3 mt-2 space-y-2" data-testid="fallback-actions">
                  {/* A) GPS works, camera failed → GPS-only submit. */}
                  {location && (allowGpsOnly || cameraError) && (
                    <>
                      <Button
                        onClick={handleGpsOnlySubmit}
                        disabled={uploading}
                        variant="outline"
                        size="lg"
                        className="w-full h-12 text-base border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-900"
                        data-testid="gps-only-submit-button"
                      >
                        {uploading ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                        ) : (
                          <><ShieldOff className="w-4 h-4 mr-2" /> {actionLabel} without selfie (GPS only)</>
                        )}
                      </Button>
                      <p className="text-xs text-amber-800 text-center">
                        GPS captured, selfie skipped — flagged for admin review.
                      </p>
                    </>
                  )}

                  {/* B) GPS failed → "I'm Onsite, Request Verification".
                      Independent path — does NOT require GPS or camera. */}
                  {locationError && (
                    <>
                      <Button
                        onClick={handleOnsiteFallbackSubmit}
                        disabled={uploading}
                        size="lg"
                        className="w-full h-12 text-base bg-cyan-600 hover:bg-cyan-700 text-white"
                        data-testid="onsite-fallback-submit-button"
                      >
                        {uploading ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                        ) : (
                          <><HandHelping className="w-4 h-4 mr-2" /> I&apos;m onsite — Request {actionLabel}</>
                        )}
                      </Button>
                      <p className="text-xs text-cyan-800 text-center">
                        Pending Admin verification. Your attendance and Reliability Score are protected.
                      </p>
                    </>
                  )}

                  {/* C) Always-available Event Code path.
                      Independent of GPS AND camera. */}
                  <Button
                    onClick={() => setShowEventCode(true)}
                    disabled={uploading}
                    variant="outline"
                    size="lg"
                    className="w-full h-12 text-base border-cyan-400 bg-cyan-50 hover:bg-cyan-100 text-cyan-900"
                    data-testid="open-event-code-button"
                  >
                    <KeyRound className="w-4 h-4 mr-2" /> Enter event code from onsite lead
                  </Button>
                </div>
              )}

              {/* Iter-411n — Event Code entry panel. Always available. */}
              {showEventCode && !photo && (
                <div
                  className="border-t pt-3 mt-2 rounded-xl border border-cyan-300 bg-cyan-50 p-4 space-y-3"
                  data-testid="event-code-panel"
                >
                  <div className="text-sm font-semibold text-cyan-900">Event code {type === 'checkin' ? 'check-in' : 'check-out'}</div>
                  <p className="text-xs text-cyan-800">
                    Ask your onsite lead, client contact, or agency for the event
                    code and enter it here. This counts as a verified onsite {type === 'checkin' ? 'check-in' : 'check-out'}.
                  </p>
                  <input
                    type="text"
                    inputMode="text"
                    autoCapitalize="characters"
                    maxLength={12}
                    value={eventCode}
                    onChange={(e) => setEventCode(e.target.value.replace(/\s/g, '').toUpperCase())}
                    placeholder="ABC123"
                    className="w-full h-12 text-center tracking-[0.4em] font-mono text-lg rounded-lg border border-cyan-300 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    data-testid="event-code-input"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      className="flex-1 h-11"
                      onClick={() => { setShowEventCode(false); setEventCode(''); setError(null); }}
                      disabled={verifyingCode}
                      data-testid="event-code-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 h-11 bg-cyan-600 hover:bg-cyan-700 text-white"
                      onClick={verifyEventCode}
                      disabled={verifyingCode || eventCode.trim().length < 4}
                      data-testid="event-code-submit-btn"
                    >
                      {verifyingCode ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Verify &amp; {actionLabel}
                    </Button>
                  </div>
                </div>
              )}

              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
          )}

          {step === 'complete' && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">
                {type === 'checkin' ? 'Check-In' : 'Check-Out'} Complete!
              </h3>
              <p className="text-muted-foreground">
                {isOutOfRadius
                  ? 'Your submission is pending admin review due to location.'
                  : 'Your submission has been verified.'}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CheckInOutModal;
